import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './Users.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';

const CountriesList = forwardRef(({ onSelectCountry, selectedCountryId, onNew }, ref) => {
  const { t } = useTranslation('admin');
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchCountries
  }));

  useEffect(() => {
    fetchCountries();
  }, [statusFilter]);

  const fetchCountries = async () => {
    setLoading(true);
    try {
      let url = getApiUrl('/api/countries');
      if (statusFilter !== 'all') {
        url += `?is_active=${statusFilter === 'active'}`;
      }

      const response = await apiFetch(url);

      if (response.ok) {
        const data = await response.json();
        setCountries(data.countries);
      } else {
        setError(t('countries.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
      setError(t('countries.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredCountries = countries.filter(country => {
    const matchesSearch = (
      country.country_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.country_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (country.currency_code && country.currency_code.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return matchesSearch;
  });

  const getStatusBadge = (isActive) => {
    return isActive ? (
      <span className="status-badge status-active">{t('countries.filters.status.active')}</span>
    ) : (
      <span className="status-badge status-inactive">{t('countries.filters.status.inactive')}</span>
    );
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && countries.length === 0) {
    return <div className="list-container"><div className="loading">{t('countries.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('countries.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('countries.newCountry')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('countries.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('countries.filters.allStatus')}</option>
            <option value="active">{t('countries.filters.status.active')}</option>
            <option value="inactive">{t('countries.filters.status.inactive')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('countries.tableHeaders.code')}</th>
              <th>{t('countries.tableHeaders.name')}</th>
              <th>{t('countries.tableHeaders.currency')}</th>
              <th>{t('countries.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredCountries.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">
                  {searchTerm ? t('countries.noData.searching') : t('countries.noData.empty')}
                </td>
              </tr>
            ) : (
              filteredCountries.map(country => (
                <tr
                  key={country.country_id}
                  onClick={() => onSelectCountry(country.country_id)}
                  className={selectedCountryId === country.country_id ? 'selected' : ''}
                >
                  <td>
                    <span className="dept-code">{country.country_code}</span>
                  </td>
                  <td>
                    <strong>{country.country_name}</strong>
                  </td>
                  <td>
                    <span>{country.currency_code || '-'}</span>
                  </td>
                  <td>
                    {getStatusBadge(country.is_active)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default CountriesList;
